import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CollapseBar, { BarTitle } from "../components/CollapseBar";
import { MoneyInput } from "../components/MoneyInput";
import { ListTools, useListPrefs } from "../components/ListTools";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import ConfirmDialog from "../components/ConfirmDialog";
import {
  PackageCheck, Plus, Trash2, Truck, Send, MessageSquare, ArrowRightCircle,
  CalendarClock, Archive, MapPin, Boxes, UserCog, Save, Settings2, X, GripVertical, Eye, Lock, Unlock,
} from "lucide-react";
import { toast } from "sonner";
import { trUpper } from "../lib/mw";
import TirSimulator from "../components/TirSimulator";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import KalemEditor from "../components/KalemEditor";

const today = () => new Date().toISOString().slice(0, 10);
const tomorrow = () => new Date(Date.now() + 864e5).toISOString().slice(0, 10);
const tl = (n) => (Number(n) || 0).toLocaleString("tr-TR");
const cellInp = "h-6 w-full min-w-[56px] rounded border border-transparent bg-transparent px-1 text-xs text-slate-100 hover:border-slate-700 focus:border-blue-500 focus:bg-slate-800 focus:outline-none";
const MONEY = ["tutar", "komple_navlun"];
function EditCell({ p, k, list, type, opts, onSave, w }) {
  const v0 = p.data?.[k] ?? "";
  const [v, setV] = useState(v0);
  useEffect(() => setV(v0), [v0]);
  const commit = (x) => { if (String(x) !== String(v0)) onSave(p, k, x); };
  if (MONEY.includes(k)) return <MoneyInput data-testid={`op-edit-${k}-${p.id}`} value={v} style={w ? { width: w } : undefined} onChange={setV} onBlur={commit} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className={cellInp + " text-right"} />;
  if (opts) return (
    <select data-testid={`op-edit-${k}-${p.id}`} value={v} onChange={(e) => { setV(e.target.value); commit(e.target.value); }} className={cellInp + " cursor-pointer"}>
      {[...new Set(["", ...opts, v].filter((x, i) => x !== "" || i === 0))].map((o) => <option key={o} value={o}>{o || "—"}</option>)}
    </select>
  );
  return <input data-testid={`op-edit-${k}-${p.id}`} type={type || "text"} list={list} value={v} style={w ? { width: w } : undefined}
    onChange={(e) => setV(k === "aciklama" || k.includes("tel") ? e.target.value : trUpper(e.target.value))}
    onBlur={(e) => commit(e.target.value)} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className={cellInp} />;
}

const EMPTY = {
  islem_tarihi: today(), gonderici_firma: "", alici_firma: "", gonderen_ilce: "", gonderen_il: "",
  alici_musteri: "", alici_ilce: "", alici_il: "", gonderen_adres: "", gonderen_tel: "",
  odeme_turu: "", kargo_turu: "KOLİ", yerlesim: "TABAN", adet: "1", desi: "", kg: "", tutar: "",
  boy: "", en: "", yukseklik: "", sekil: "KUTU", cap: "", rulo_yon: "YATAY", istif: "EVET",
  alim_durumu: "BEKLİYOR", surucu: "", surucu_tel: "", alim_bolge: "", musteri_no: "", kimin_adina: "", aciklama: "",
};

const FORM_FIELDS = {
  islem_tarihi: { label: "TARİH", type: "date" },
  gonderici_firma: { label: "GÖNDERİCİ FİRMA", type: "firma", who: "gonderici" },
  gonderen_il: { label: "GÖND. İL", type: "province", dkey: "gonderen_ilce" },
  gonderen_ilce: { label: "GÖND. İLÇE", type: "district", pkey: "gonderen_il" },
  gonderen_tel: { label: "GÖND. TEL", type: "text" },
  gonderen_adres: { label: "GÖND. ADRES", type: "text" },
  alici_firma: { label: "ALICI FİRMA", type: "firma", who: "alici" },
  alici_musteri: { label: "ALICI MÜŞTERİ/YETKİLİ", type: "text" },
  alici_il: { label: "ALICI İL", type: "province", dkey: "alici_ilce" },
  alici_ilce: { label: "ALICI İLÇE", type: "district", pkey: "alici_il" },
  odeme_turu: { label: "ÖDEME (ÜA/PÖ)", type: "text", list: "odeme-list" },
  kargo_turu: { label: "TÜR (SERBEST: KOLİ/PALET...)", type: "text", list: "kargo-list" },
  yerlesim: { label: "YERLEŞİM", type: "yerlesim" },
  adet: { label: "ADET", type: "text" },
  desi: { label: "DESİ", type: "text" },
  kg: { label: "KG (TOPLAM)", type: "text" },
  boy: { label: "BOY cm", type: "text" },
  en: { label: "EN cm", type: "text" },
  yukseklik: { label: "YÜKSEKLİK cm", type: "text" },
  sekil: { label: "ŞEKİL", type: "opt", opts: [["KUTU", "KUTU (KOLİ/PALET)"], ["RULO", "RULO / SİLİNDİR"]] },
  cap: { label: "RULO ÇAP cm", type: "text" },
  rulo_yon: { label: "RULO YÖNÜ", type: "opt", opts: [["YATAY", "YATAY"], ["DİKEY", "DİKEY"]] },
  istif: { label: "ÜSTÜNE YÜK", type: "opt", opts: [["EVET", "KONABİLİR"], ["HAYIR", "KONAMAZ"]] },
  tutar: { label: "TUTAR ₺", type: "text" },
  alim_durumu: { label: "DURUM", type: "text", list: "durum-list" },
  alim_bolge: { label: "ALIM BÖLGE", type: "text" },
  arac_sec: { label: "KAYITLI ARAÇ (SÜRÜCÜ/PLAKA)", type: "vehicle" },
  surucu: { label: "SÜRÜCÜ + PLAKA", type: "text" },
  surucu_tel: { label: "SÜRÜCÜ TEL", type: "text" },
  musteri_no: { label: "ALIM MÜŞTERİ NO", type: "text" },
  kimin_adina: { label: "KİMİN ADINA", type: "text" },
  aciklama: { label: "AÇIKLAMA (SMS'E GİDER)", type: "text" },
};
const DEFAULT_FORM_ORDER = [
  "islem_tarihi", "gonderici_firma", "gonderen_il", "gonderen_ilce", "gonderen_tel", "gonderen_adres",
  "alici_firma", "alici_musteri", "alici_il", "alici_ilce", "odeme_turu", "kargo_turu", "yerlesim",
  "adet", "desi", "kg", "boy", "en", "yukseklik", "sekil", "cap", "rulo_yon", "istif", "tutar", "alim_durumu", "alim_bolge", "surucu", "surucu_tel", "musteri_no", "kimin_adina", "aciklama",
];

export default function Operasyon({ view = "list" }) {
  const prefs = useListPrefs("kargo-alim");
  const { isAdmin } = useAuth();
  const [date, setDate] = useState(today());
  const [archived, setArchived] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ genel: { palet: 0, koli: 0, kasa: 0, adet: 0, ciro: 0, kayit: 0 }, il: [], ilce: [], bolge: [] });
  const [tirs, setTirs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [tir, setTir] = useState({ plate: "", driver: "", yon: "", hatlar: "", yerlesim: "TABAN", ciro: "", navlun: "" });
  const [autoDays, setAutoDays] = useState(3);
  useEffect(() => { api.get("/op-settings", { silent: true }).then((r) => setAutoDays(r.data.auto_days)).catch(() => {}); }, []);
  const [confirm, setConfirm] = useState(null);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState({});
  const [customers, setCustomers] = useState([]);
  const [cols, setCols] = useState([]);
  const [opFields, setOpFields] = useState([]);
  const [formOrder, setFormOrder] = useState(DEFAULT_FORM_ORDER);
  const [editCols, setEditCols] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [opts, setOpts] = useState({ kargo_turu: [], alim_durumu: [], yerlesim: [] });
  const loadOpts = () => ["kargo_turu", "alim_durumu", "yerlesim"].forEach((k) => api.get(`/options/${k}`, { silent: true }).then((r) => setOpts((o) => ({ ...o, [k]: r.data }))).catch(() => {}));
  const addOpt = async (k) => { const v = window.prompt("YENİ SEÇENEK"); if (!v) return; await api.post(`/options/${k}`, { data: { value: v } }); loadOpts(); set(k, trUpper(v)); toast.success("SEÇENEK EKLENDİ"); };
  const dragIdx = useRef(null);
  const formDrag = useRef(null);

  const custByName = useMemo(() => {
    const m = {};
    customers.forEach((c) => { const n = (c.data?.firma || "").trim().toUpperCase(); if (n) m[n] = c.data; });
    return m;
  }, [customers]);
  const custNames = useMemo(() => [...new Set(customers.map((c) => c.data?.firma).filter(Boolean))], [customers]);

  const load = useCallback(() => {
    api.get("/pickups", { params: { date, archived: String(archived) } }).then((r) => setRows(r.data));
    api.get("/pickups/summary", { params: { date } }).then((r) => setSummary(r.data));
    api.get("/tirs").then((r) => setTirs(r.data));
  }, [date, archived]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/geo/provinces", { silent: true }).then((r) => setProvinces(r.data)).catch(() => {});
    api.get("/vehicles", { silent: true }).then((r) => setVehicles(r.data)).catch(() => {});
    loadOpts();
    api.get("/customers?page_size=1000", { silent: true }).then((r) => setCustomers(r.data.items || [])).catch(() => {});
    api.get("/op-config", { silent: true }).then((r) => { setCols(r.data.columns || []); setOpFields(r.data.fields || []); setFormOrder((r.data.form && r.data.form.length) ? r.data.form : DEFAULT_FORM_ORDER); }).catch(() => {});
  }, []);

  const loadDistricts = useCallback((prov) => {
    if (!prov || districts[prov]) return;
    api.get(`/geo/districts/${encodeURIComponent(prov)}`, { silent: true }).then((r) => setDistricts((d) => ({ ...d, [prov]: r.data }))).catch(() => {});
  }, [districts]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // customer autofill
  const pickCustomer = (name, who) => {
    const d = custByName[(name || "").trim().toUpperCase()];
    if (!d) return;
    if (who === "gonderici") {
      setForm((f) => ({ ...f, gonderici_firma: d.firma, gonderen_il: d.il || "", gonderen_ilce: d.ilce || "", gonderen_tel: d.gsm || d.s_tel || "", gonderen_adres: d.adres || "" }));
      loadDistricts(d.il);
    } else {
      setForm((f) => ({ ...f, alici_firma: d.firma, alici_musteri: d.yetkili || f.alici_musteri, alici_il: d.il || "", alici_ilce: d.ilce || "" }));
      loadDistricts(d.il);
    }
  };

  const add = async () => {
    if (!form.gonderici_firma.trim()) { toast.error("GÖNDERİCİ FİRMA GEREKLİ"); return; }
    await api.post("/pickups", { data: { ...form, islem_tarihi: form.islem_tarihi || date } });
    toast.success("ALIM EKLENDİ");
    setForm({ ...EMPTY, islem_tarihi: form.islem_tarihi || date, alim_bolge: form.alim_bolge });
    load();
  };
  const saveNewCustomer = async (name, il, ilce, tel, adres) => {
    const nm = (name || "").trim();
    if (!nm) { toast.error("FİRMA ADI GEREKLİ"); return; }
    if (custByName[nm.toUpperCase()]) { toast.info("ZATEN KAYITLI"); return; }
    await api.post("/customers", { data: { firma: trUpper(nm), il: il || "", ilce: ilce || "", gsm: tel || "", adres: adres || "" } });
    toast.success("MÜŞTERİ KAYDEDİLDİ");
    const r = await api.get("/customers?page_size=1000", { silent: true });
    setCustomers(r.data.items || []);
  };

  const del = async () => { await api.delete(`/pickups/${confirm}`); setConfirm(null); toast.success("SİLİNDİ"); load(); };
  const assignTir = async (p, tid) => { await api.put(`/pickups/${p.id}`, { data: p.data, tir_id: tid }); load(); };
  const toCargo = async (p) => { await api.post(`/pickups/${p.id}/to-cargo`); toast.success("KARGO GİRİŞİNE AKTARILDI"); load(); };
  const sms = async (p) => {
    const r = await api.post(`/pickups/${p.id}/sms`);
    if (r.data.sent) toast.success("SÜRÜCÜYE SMS GÖNDERİLDİ");
    else if (!r.data.has_phone) toast.error("SÜRÜCÜ TELEFONU GİRİLMEMİŞ");
    else if (!r.data.has_twilio) toast.error("TWILIO AYARLANMAMIŞ (AYARLAR > ENTEGRASYONLAR)");
    else toast.error("SMS GÖNDERİLEMEDİ");
  };
  const rollover = async () => { const r = await api.post("/pickups/rollover", { from_date: date, to_date: tomorrow() }); toast.success(`${r.data.moved} ALIM ${r.data.to} TARİHİNE DEVREDİLDİ`); load(); };
  const archiveDay = async () => { const r = await api.post("/pickups/archive", { date }); toast.success(`${r.data.archived} ALIM ARŞİVLENDİ`); load(); };
  const addTir = async () => {
    if (!tir.plate.trim()) { toast.error("PLAKA GEREKLİ"); return; }
    const r = await api.post("/tirs", { ...tir, ciro: Number(tir.ciro) || 0, navlun: Number(tir.navlun) || 0, hatlar: tir.hatlar.split(",").map((s) => s.trim()).filter(Boolean) });
    toast.success(`TIR / HAT AÇILDI${r.data.auto_eklenen ? ` · ${r.data.auto_eklenen} ALIM OTOMATİK EKLENDİ` : ""}`); setTir({ plate: "", driver: "", yon: "", hatlar: "", yerlesim: "TABAN", ciro: "", navlun: "" }); load();
  };
  const delTir = async (t) => { await api.delete(`/tirs/${t.id}`); load(); };
  const [tirDetail, setTirDetail] = useState(null);
  const toggleTir = async (t) => { await api.put(`/tirs/${t.id}`, { plate: t.plate, driver: t.driver, yon: t.yon, hatlar: t.hatlar, yerlesim: t.yerlesim, closed: !t.closed, ciro: t.ciro, navlun: t.navlun }); toast.success(t.closed ? "TIR AÇILDI" : "TIR KAPATILDI"); load(); };
  const openTirDetail = async (t) => { const r = await api.get(`/tirs/${t.id}/pickups`); setTirDetail({ tir: t, pickups: r.data }); };
  const reassign = async (p) => {
    const n = window.prompt("YENİ SÜRÜCÜ (AD + PLAKA)", p.data?.surucu || "");
    if (n === null) return;
    await api.put(`/pickups/${p.id}`, { data: { ...p.data, surucu: trUpper(n) }, tir_id: p.tir_id || "" });
    toast.success("SÜRÜCÜ DEĞİŞTİRİLDİ"); load();
  };

  const drivers = useMemo(() => Object.values(rows.reduce((a, p) => {
    const k = (p.data?.surucu || "—");
    a[k] = a[k] || { surucu: k, adet: 0, kayit: 0, ciro: 0 };
    a[k].kayit++; a[k].adet += Number(p.data?.adet) || 0; a[k].ciro += Number(p.data?.tutar) || 0;
    return a;
  }, {})), [rows]);

  // ---- config persistence (form + list columns + dynamic fields) ----
  const persist = async (patch) => {
    const next = { columns: patch.columns ?? cols, fields: patch.fields ?? opFields, form: patch.form ?? formOrder };
    setCols(next.columns); setOpFields(next.fields); setFormOrder(next.form);
    try { await api.put("/op-config", { data: next }); }
    catch (e) { toast.error("KAYDEDİLEMEDİ (YETKİ GEREKLİ)"); }
  };
  const onDrop = (i) => {
    const from = dragIdx.current; if (from === null || from === i) return;
    const next = [...cols]; const [m] = next.splice(from, 1); next.splice(i, 0, m); dragIdx.current = null;
    persist({ columns: next });
  };
  const removeCol = (key) => persist({ columns: cols.filter((c) => c.key !== key), fields: opFields.filter((f) => f.key !== key) });
  const onDropForm = (i) => {
    const from = formDrag.current; if (from === null || from === i) return;
    const next = [...formOrder]; const [m] = next.splice(from, 1); next.splice(i, 0, m); formDrag.current = null;
    persist({ form: next });
  };
  const removeFormField = (key) => persist({ form: formOrder.filter((k) => k !== key) });
  const addFormField = (key) => persist({ form: [...formOrder, key] });
  const addField = () => {
    const label = window.prompt("YENİ BAŞLIK / ALAN ADI");
    if (!label || !label.trim()) return;
    const key = "x_" + label.trim().toLowerCase().replace(/[^a-z0-9]+/gi, "_").replace(/_+/g, "_").replace(/^_|_$/g, "") + "_" + Math.random().toString(36).slice(2, 5);
    const f = { key, label: trUpper(label.trim()) };
    persist({ columns: [...cols, { key, label: f.label }], fields: [...opFields, f], form: [...formOrder, key] });
    toast.success("ALAN EKLENDİ");
  };
  const resetCols = () => { api.put("/op-config", { data: { columns: [], fields: [], form: [] } }).then(() => api.get("/op-config").then((r) => { setCols(r.data.columns || []); setOpFields(r.data.fields || []); setFormOrder(DEFAULT_FORM_ORDER); })); };
  const hiddenFormFields = [
    ...Object.keys(FORM_FIELDS).filter((k) => !formOrder.includes(k)).map((k) => ({ key: k, label: FORM_FIELDS[k].label })),
    ...opFields.filter((f) => !formOrder.includes(f.key)),
  ];

  const DURUM_BADGE = (v) => <span className={`rounded px-1.5 py-0.5 ${v === "ALINDI" ? "bg-emerald-500/20 text-emerald-300" : v === "ERTELENDİ" ? "bg-amber-500/20 text-amber-300" : "bg-slate-700 text-slate-300"}`}>{v}</span>;
  const saveCell = async (p, k, v) => {
    const data = { ...p.data, [k]: v };
    setRows((rs) => rs.map((x) => (x.id === p.id ? { ...x, data } : x)));
    await api.put(`/pickups/${p.id}`, { data, tir_id: p.tir_id || "" }, { silent: true });
  };
  const DURUMLAR = [...new Set(["BEKLİYOR", "ALINDI", "ERTELENDİ", "İPTAL", ...opts.alim_durumu])];
  const editCell = (col, p) => {
    const E = (k, extra = {}) => <EditCell p={p} k={k} onSave={saveCell} {...extra} />;
    switch (col.key) {
      case "islem_tarihi": return E("islem_tarihi", { type: "date" });
      case "gonderici_firma": case "alici_firma": return E(col.key, { list: "firma-list" });
      case "gonderen_konum": return <div className="flex gap-0.5">{E("gonderen_ilce", { w: 80 })}<span className="text-slate-600">/</span>{E("gonderen_il", { w: 80 })}</div>;
      case "alici_konum": return <div className="flex gap-0.5">{E("alici_ilce", { w: 80 })}<span className="text-slate-600">/</span>{E("alici_il", { w: 80 })}</div>;
      case "odeme_turu": return E("odeme_turu", { list: "odeme-list" });
      case "kargo_turu": return E("kargo_turu", { list: "kargo-list" });
      case "alim_durumu": return E("alim_durumu", { opts: DURUMLAR });
      default: return E(col.key);
    }
  };
  const [editP, setEditP] = useState(null);
  const cellValue = (col, d) => {
    switch (col.key) {
      case "islem_tarihi": return <span className="font-mono text-slate-400">{d.islem_tarihi}</span>;
      case "gonderen_konum": return <span className="text-slate-400">{d.gonderen_ilce}/{d.gonderen_il}</span>;
      case "alici_firma": return d.alici_firma || d.alici_musteri;
      case "alici_konum": return <span className="text-slate-400">{d.alici_ilce}/{d.alici_il}</span>;
      case "kargo_turu": return <span>{d.adet}× {d.kargo_turu}</span>;
      case "tutar": return d.tutar ? tl(d.tutar) : "—";
      case "alim_durumu": return DURUM_BADGE(d.alim_durumu);
      default: return d[col.key] || "";
    }
  };

  const inpCls = "h-9 w-full rounded-md border border-slate-700 bg-slate-800 px-2 text-sm text-slate-100";
  const Txt = (k, ph, list) => MONEY.includes(k) ? <MoneyInput data-testid={`f-${k}`} key={k} placeholder={ph} value={form[k] || ""} onChange={(v) => set(k, v)} className={inpCls} /> : (
    <input data-testid={`f-${k}`} key={k} list={list} placeholder={ph} value={form[k] || ""}
      onChange={(e) => set(k, k === "aciklama" || k.includes("tel") || k === "gonderen_adres" ? e.target.value : trUpper(e.target.value))}
      className={inpCls} />
  );
  const Prov = (k, dkey, ph) => (
    <select data-testid={`f-${k}`} key={k} value={form[k] || ""} onChange={(e) => { set(k, e.target.value); set(dkey, ""); loadDistricts(e.target.value); }} className={inpCls}>
      <option value="">{ph}</option>{provinces.map((p) => <option key={p} value={p}>{p}</option>)}
    </select>
  );
  const Dist = (k, provKey, ph) => {
    const list = districts[form[provKey]] || [];
    return (
      <select data-testid={`f-${k}`} key={k} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className={inpCls}>
        <option value="">{ph}</option>{list.map((p) => <option key={p} value={p}>{p}</option>)}
        {form[k] && !list.includes(form[k]) && <option value={form[k]}>{form[k]}</option>}
      </select>
    );
  };
  const Firma = (who, k) => (
    <div className="flex w-full gap-1">
      <input data-testid={`f-${k}`} list="firma-list" placeholder={FORM_FIELDS[k].label} value={form[k] || ""}
        onChange={(e) => set(k, trUpper(e.target.value))} onBlur={(e) => pickCustomer(e.target.value, who)}
        className={inpCls + " min-w-0 flex-1"} />
      <button data-testid={`save-${who}`} title="YENİ MÜŞTERİ KAYDET"
        onClick={() => who === "gonderici" ? saveNewCustomer(form.gonderici_firma, form.gonderen_il, form.gonderen_ilce, form.gonderen_tel, form.gonderen_adres) : saveNewCustomer(form.alici_firma, form.alici_il, form.alici_ilce)}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-700 bg-slate-800 text-emerald-400 hover:bg-slate-700"><Save className="h-4 w-4" /></button>
    </div>
  );
  const renderCtrl = (key) => {
    const meta = FORM_FIELDS[key] || { label: (opFields.find((f) => f.key === key)?.label) || key, type: "text" };
    switch (meta.type) {
      case "date": return <input data-testid={`f-${key}`} type="date" value={form[key] || ""} onChange={(e) => set(key, e.target.value)} className={inpCls} />;
      case "firma": return Firma(meta.who, key);
      case "province": return Prov(key, meta.dkey, meta.label);
      case "district": return Dist(key, meta.pkey, meta.label);
      case "yerlesim": return (
        <div className="flex w-full gap-1">
          <select data-testid={`f-${key}`} value={form[key] || "TABAN"} onChange={(e) => set(key, e.target.value)} className={inpCls + " min-w-0 flex-1"}>
            <option value="TABAN">TABAN (ALTA YÜKLE)</option><option value="ÜST">ÜST (PALET ÜSTÜ)</option><option value="FARKETMEZ">FARKETMEZ / KONULABİLİR</option>
            {opts.yerlesim.map((o) => <option key={o} value={o}>{o}</option>)}
            {form[key] && !["TABAN", "ÜST", "FARKETMEZ", ...opts.yerlesim].includes(form[key]) && <option value={form[key]}>{form[key]}</option>}
          </select>
          <button data-testid="opt-add-yerlesim" title="SEÇENEK EKLE" onClick={() => addOpt("yerlesim")} className="flex h-9 w-7 shrink-0 items-center justify-center rounded-md border border-slate-700 bg-slate-800 text-emerald-400"><Plus className="h-3.5 w-3.5" /></button>
        </div>);
      case "vehicle": return (
        <select data-testid="f-arac_sec" value={form.arac_id || ""} onChange={(e) => { const x = vehicles.find((q) => String(q.id) === e.target.value); setForm((f) => ({ ...f, arac_id: e.target.value, ...(x ? { surucu: [x.surucu, x.cekici_plaka, x.dorse_plaka].filter(Boolean).join(" "), surucu_tel: x.surucu_tel || f.surucu_tel } : {}) })); }} className={inpCls}>
          <option value="">{meta.label}</option>{vehicles.map((x) => <option key={x.id} value={x.id}>{`${x.cekici_plaka} · ${x.surucu} · ${x.arac_tipi} · ${x.hizmet}`}</option>)}
        </select>);
      case "opt": return <select data-testid={`f-${key}`} value={form[key] || meta.opts[0][0]} onChange={(e) => set(key, e.target.value)} className={inpCls}>{meta.opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>;
      default:
        if (key === "kargo_turu" || key === "alim_durumu") return (
          <div className="flex w-full gap-1"><div className="min-w-0 flex-1">{Txt(key, meta.label, meta.list)}</div>
            <button data-testid={`opt-add-${key}`} title="SEÇENEK EKLE" onClick={() => addOpt(key)} className="flex h-9 w-7 shrink-0 items-center justify-center rounded-md border border-slate-700 bg-slate-800 text-emerald-400"><Plus className="h-3.5 w-3.5" /></button></div>);
        return Txt(key, meta.label, meta.list);
    }
  };

  return (
    <div className="mw-fade-up space-y-4" data-testid="operasyon-page">
      <BarTitle><div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-bold tracking-tight text-white flex items-center gap-2"><PackageCheck className="h-7 w-7 text-orange-400" /> {view === "ozet" ? "ALIM ÖZET" : "KARGO ALIMI / OPERASYON"}</h1>
          <p className="text-sm text-slate-400">GÜNLÜK ALIMLAR · TIR YÜKLEME · İLÇE/BÖLGE/CİRO ÖZET</p>
        </div>
        <CollapseBar id="op-actions" label="İŞLEMLER" className="flex-1 justify-end">
          <Input data-testid="op-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-9 w-40 border-slate-700 bg-slate-800 text-slate-100" />
          <Button data-testid="op-archived-toggle" variant="outline" onClick={() => setArchived((a) => !a)} className={`border-slate-700 ${archived ? "bg-amber-950/40 text-amber-300" : "bg-slate-800 text-slate-200"}`}>{archived ? "ARŞİV" : "AKTİF"}</Button>
          <Button data-testid="op-rollover" variant="outline" onClick={rollover} className="border-slate-700 bg-slate-800 text-slate-200"><CalendarClock className="mr-1 h-4 w-4" /> ERTESİ GÜNE DEVRET</Button>
          <Button data-testid="op-archive" variant="outline" onClick={archiveDay} className="border-slate-700 bg-slate-800 text-slate-200"><Archive className="mr-1 h-4 w-4" /> GÜNÜ ARŞİVLE</Button>
          {isAdmin && <Button data-testid="op-editcols" variant="outline" onClick={() => setEditCols((v) => !v)} className={`border-slate-700 ${editCols ? "bg-orange-950/40 text-orange-300" : "bg-slate-800 text-slate-200"}`}><Settings2 className="mr-1 h-4 w-4" /> KOLON DÜZENLE</Button>}
        </CollapseBar>
      </div></BarTitle>

      {editCols && isAdmin && (
        <div className="flex flex-wrap items-center gap-2 rounded-xl border border-orange-900/40 bg-orange-950/20 p-3 text-xs text-orange-200">
          <span className="font-semibold">DÜZENLEME AÇIK:</span> form alanlarını ve liste başlıklarını sürükleyip sırala, × ile kaldır.
          <Button data-testid="op-addfield" size="sm" onClick={addField} className="bg-orange-600 text-white hover:bg-orange-700"><Plus className="mr-1 h-3.5 w-3.5" /> ALAN / BAŞLIK EKLE</Button>
          <Button data-testid="op-resetcols" size="sm" variant="outline" onClick={resetCols} className="border-orange-800 bg-transparent text-orange-200">VARSAYILANA DÖN</Button>
          {hiddenFormFields.length > 0 && (
            <div className="flex w-full flex-wrap items-center gap-1 border-t border-orange-900/40 pt-2">
              <span className="text-[11px] font-semibold">GİZLİ ALANLAR (EKLEMEK İÇİN TIKLA):</span>
              {hiddenFormFields.map((h) => (
                <button key={h.key} data-testid={`form-add-${h.key}`} onClick={() => addFormField(h.key)} className="rounded-full border border-orange-700 bg-orange-900/30 px-2 py-0.5 text-[11px] text-orange-100 hover:bg-orange-800/50"><Plus className="mr-0.5 inline h-3 w-3" />{h.label}</button>
              ))}
            </div>
          )}
        </div>
      )}

      <datalist id="firma-list">{custNames.map((s) => <option key={s} value={s} />)}</datalist>
      <datalist id="odeme-list"><option value="ÜA" /><option value="PÖ" /></datalist>
      <datalist id="kargo-list">{[...new Set(["KOLİ", "PALET", "KASA", "RULO", "KOMPLE ARAÇ", ...opts.kargo_turu])].map((o) => <option key={o} value={o} />)}</datalist>
      <datalist id="durum-list">{[...new Set(["BEKLİYOR", "ALINDI", "ERTELENDİ", "İPTAL", ...opts.alim_durumu])].map((o) => <option key={o} value={o} />)}</datalist>

      {view === "list" && !archived && (
        <div className="rounded-xl border border-slate-800 bg-slate-900 p-3">
          <div className="grid grid-cols-2 gap-x-2 gap-y-3 sm:grid-cols-4 lg:grid-cols-6">
            {formOrder.map((key, i) => (
              <div key={key} className={`relative min-w-0 ${editCols && isAdmin ? "rounded-md ring-1 ring-orange-500/40" : ""}`}
                draggable={editCols && isAdmin}
                onDragStart={() => { formDrag.current = i; }}
                onDragOver={(e) => editCols && e.preventDefault()}
                onDrop={() => editCols && onDropForm(i)}>
                {editCols && isAdmin && (
                  <div className="absolute -top-2 right-0 z-10 flex items-center gap-0.5">
                    <span className="cursor-move rounded bg-orange-600 px-1 text-[9px] leading-4 text-white">⋮⋮</span>
                    <button data-testid={`form-del-${key}`} onClick={() => removeFormField(key)} className="flex h-4 w-4 items-center justify-center rounded bg-rose-600 text-white"><X className="h-3 w-3" /></button>
                  </div>
                )}
                {renderCtrl(key)}
              </div>
            ))}
            {!formOrder.includes("arac_sec") && <div className="min-w-0">{renderCtrl("arac_sec")}</div>}
            {form.kargo_turu === "KOMPLE ARAÇ" && (
              <div data-testid="komple-box" className="col-span-full grid grid-cols-2 gap-2 rounded-md border border-amber-600/50 bg-amber-950/20 p-2 sm:grid-cols-5">
                <div className="col-span-full text-[11px] font-bold text-amber-300">KOMPLE ARAÇ — NAVLUN, SATIŞ VE YÜK ŞEKLİ</div>
                <MoneyInput data-testid="f-komple_navlun" placeholder="NAVLUN (MALİYET) ₺" value={form.komple_navlun || ""} onChange={(v) => set("komple_navlun", v)} className={inpCls} />
                <MoneyInput data-testid="f-komple_satis" placeholder="İŞİN TUTARI (SATIŞ) ₺" value={form.tutar || ""} onChange={(v) => set("tutar", v)} className={inpCls} />
                <select data-testid="f-komple_yuk" value={form.komple_yuk || "KOMPLE PALET"} onChange={(e) => set("komple_yuk", e.target.value)} className={inpCls}><option>KOMPLE PALET</option><option>KOMPLE KOLİ</option><option>KARIŞIK (PALET + PARÇA)</option><option>DÖKME / DİĞER</option></select>
                <input data-testid="f-komple_palet" placeholder="KAÇ PALET" value={form.komple_palet || ""} onChange={(e) => set("komple_palet", e.target.value)} className={inpCls} />
                <input data-testid="f-komple_parca" placeholder="KAÇ PARÇA / KOLİ" value={form.komple_parca || ""} onChange={(e) => set("komple_parca", e.target.value)} className={inpCls} />
                {form.tutar && form.komple_navlun && <div className="col-span-full text-xs font-semibold text-emerald-300">KÂR: {(Number(String(form.tutar).replace(",", ".")) - Number(String(form.komple_navlun).replace(",", "."))).toLocaleString("tr-TR")}</div>}
              </div>
            )}
            <Button data-testid="op-add" onClick={add} className="h-9 w-full bg-orange-600 text-white hover:bg-orange-700"><Plus className="mr-1 h-4 w-4" /> ALIM EKLE</Button>
          </div>
        </div>
      )}

      <div className={view === "list" ? "grid grid-cols-1 gap-4" : "grid grid-cols-1 gap-4 lg:grid-cols-2"}>
        {view === "list" && <div className="space-y-1"><div className="flex justify-end"><ListTools id="kargo-alim" prefs={prefs} cols={cols.map((c) => [c.key, c.label])} canAdd={false} /></div><div className="overflow-auto rounded-xl border border-slate-800 bg-slate-900 max-h-[70vh]">
          <table data-testid="op-table" {...prefs.tableProps} className="mw-dens w-full">
            <thead className="sticky top-0 bg-slate-900"><tr className="border-b border-slate-800 text-left uppercase tracking-wider text-slate-500">
              {cols.filter((c) => !prefs.hidden.includes(c.key)).map((col, i) => (
                <th key={col.key} className={`px-2 py-2 ${editCols ? "cursor-move select-none" : ""}`}
                  draggable={editCols && isAdmin}
                  onDragStart={() => { dragIdx.current = i; }}
                  onDragOver={(e) => editCols && e.preventDefault()}
                  onDrop={() => editCols && onDrop(i)}>
                  <span className="inline-flex items-center gap-1">
                    {editCols && <GripVertical className="h-3 w-3 text-orange-400" />}
                    {col.label}
                    {editCols && <button data-testid={`col-del-${col.key}`} onClick={() => removeCol(col.key)} className="text-rose-400 hover:text-rose-300"><X className="h-3 w-3" /></button>}
                  </span>
                </th>
              ))}
              <th className="px-2 py-2">TIR</th>
              <th className="px-2 py-2 text-right">İŞLEM</th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={cols.length + 2} className="px-3 py-10 text-center text-slate-500">KAYIT YOK</td></tr>}
              {rows.map((p) => { const d = p.data || {}; return (
                <tr key={p.id} data-testid={`op-row-${p.id}`} className="border-b border-slate-800/60 text-slate-200">
                  {cols.filter((c) => !prefs.hidden.includes(c.key)).map((col) => <td key={col.key} className="px-1 py-0.5">{archived ? cellValue(col, d) : editCell(col, p)}</td>)}
                  <td className="px-2 py-1.5">
                    <select data-testid={`op-tir-${p.id}`} value={p.tir_id || ""} onChange={(e) => assignTir(p, e.target.value)} className="h-7 rounded border border-slate-700 bg-slate-800 px-1 text-xs text-slate-100">
                      <option value="">—</option>{tirs.map((t) => <option key={t.id} value={t.id}>{t.plate || t.id}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`op-edit-${p.id}`} title="TÜMÜNÜ DÜZENLE (KOLİ / MÜŞTERİ / ÖLÇÜ)" onClick={() => setEditP(p)} className="rounded p-1 text-slate-400 hover:text-sky-400"><Settings2 className="h-4 w-4" /></button>
                      <button data-testid={`op-cargo-${p.id}`} title="KARGOYA AKTAR" onClick={() => toCargo(p)} className="rounded p-1 text-slate-400 hover:text-blue-400"><ArrowRightCircle className="h-4 w-4" /></button>
                      <button data-testid={`op-sms-${p.id}`} title="SÜRÜCÜYE SMS" onClick={() => sms(p)} className="rounded p-1 text-slate-400 hover:text-emerald-400"><MessageSquare className="h-4 w-4" /></button>
                      <button data-testid={`op-reassign-${p.id}`} title="SÜRÜCÜ DEĞİŞTİR / DEVRET" onClick={() => reassign(p)} className="rounded p-1 text-slate-400 hover:text-amber-400"><UserCog className="h-4 w-4" /></button>
                      <button data-testid={`op-del-${p.id}`} title="SİL" onClick={() => setConfirm(p.id)} className="rounded p-1 text-slate-400 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ); })}
            </tbody>
          </table>
        </div></div>}

        {view === "ozet" && <div className="contents">
          <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-200"><Boxes className="h-4 w-4 text-orange-400" /> GÜNLÜK TOPLAM</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-slate-800 p-2"><div className="text-lg font-bold text-blue-300">{summary.genel.palet}</div><div className="text-xs text-slate-500">PALET</div></div>
              <div className="rounded-lg bg-slate-800 p-2"><div className="text-lg font-bold text-emerald-300">{summary.genel.koli}</div><div className="text-xs text-slate-500">KOLİ</div></div>
              <div className="rounded-lg bg-slate-800 p-2"><div className="text-lg font-bold text-amber-300">{summary.genel.kasa}</div><div className="text-xs text-slate-500">KASA</div></div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-center">
              <div className="rounded-lg bg-slate-800 p-2"><div className="text-lg font-bold text-slate-200">{summary.genel.adet}</div><div className="text-xs text-slate-500">ADET</div></div>
              <div className="rounded-lg bg-slate-800 p-2"><div className="text-lg font-bold text-orange-300">{tl(summary.genel.ciro)}</div><div className="text-xs text-slate-500">CİRO</div></div>
            </div>
            <div className="mt-3 max-h-40 overflow-auto">
              <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-slate-400"><MapPin className="h-3 w-3" /> İLÇE BAZLI (ADET · CİRO)</div>
              {summary.ilce.length === 0 && <div className="text-xs text-slate-600">—</div>}
              {summary.ilce.map((x) => <div key={x.ilce} className="flex justify-between border-b border-slate-800/60 py-1 text-xs text-slate-300"><span className="truncate">{x.ilce}</span><span className="text-slate-400">{x.adet} adet · {tl(x.ciro)}</span></div>)}
            </div>
            <div className="mt-3 max-h-32 overflow-auto">
              <div className="mb-1 text-xs font-semibold text-slate-400">ALIM BÖLGE BAZLI (ADET · CİRO)</div>
              {summary.bolge.length === 0 && <div className="text-xs text-slate-600">—</div>}
              {summary.bolge.map((x) => <div key={x.bolge} className="flex justify-between border-b border-slate-800/60 py-1 text-xs text-slate-300"><span className="truncate">{x.bolge}</span><span className="text-slate-400">{x.adet} adet · {tl(x.ciro)}</span></div>)}
            </div>
            <div className="mt-3 max-h-40 overflow-auto">
              <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-slate-400"><UserCog className="h-3 w-3" /> SÜRÜCÜLER (ADET · CİRO)</div>
              {drivers.length === 0 && <div className="text-xs text-slate-600">—</div>}
              {drivers.map((d) => <div key={d.surucu} className="flex justify-between border-b border-slate-800/60 py-1 text-xs text-slate-300"><span className="truncate">{d.surucu}</span><span className="text-slate-400">{d.kayit} alım · {d.adet} adet · {tl(d.ciro)}</span></div>)}
            </div>
          </div>

          <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-200"><Truck className="h-4 w-4 text-sky-400" /> TIR / HAT AÇ
              {isAdmin && <label className="ml-auto flex items-center gap-1 text-[11px] font-semibold text-slate-400" title="TIR AÇILINCA SON KAÇ GÜNÜN ALIMLARI OTOMATİK EKLENSİN">OTO. EKLEME SON
                <input data-testid="op-auto-days" key={autoDays} defaultValue={autoDays} onBlur={async (e) => { const r = await api.put("/op-settings", { data: { auto_days: e.target.value } }); setAutoDays(r.data.auto_days); toast.success(`SON ${r.data.auto_days} GÜN`); }} className="h-6 w-10 rounded border border-slate-700 bg-slate-800 px-1 text-center text-slate-100" /> GÜN</label>}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="tir-plate" placeholder="PLAKA (34 MW 34)" value={tir.plate} onChange={(e) => setTir((t) => ({ ...t, plate: trUpper(e.target.value) }))} className="h-9 border-slate-700 bg-slate-800 text-slate-100" />
              <Input data-testid="tir-driver" placeholder="SÜRÜCÜ" value={tir.driver} onChange={(e) => setTir((t) => ({ ...t, driver: trUpper(e.target.value) }))} className="h-9 border-slate-700 bg-slate-800 text-slate-100" />
              <Input data-testid="tir-yon" placeholder="YÖN KAPAMA" value={tir.yon} onChange={(e) => setTir((t) => ({ ...t, yon: trUpper(e.target.value) }))} className="h-9 border-slate-700 bg-slate-800 text-slate-100" />
              <Input data-testid="tir-hatlar" placeholder="HATLAR (İL,İL)" value={tir.hatlar} onChange={(e) => setTir((t) => ({ ...t, hatlar: trUpper(e.target.value) }))} className="h-9 border-slate-700 bg-slate-800 text-slate-100" />
              <MoneyInput data-testid="tir-ciro" placeholder="TOPLAM CİRO ₺" value={tir.ciro} onChange={(v) => setTir((t) => ({ ...t, ciro: v }))} className="h-9 w-full rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100" />
              <MoneyInput data-testid="tir-navlun" placeholder="NAVLUN ₺" value={tir.navlun} onChange={(v) => setTir((t) => ({ ...t, navlun: v }))} className="h-9 w-full rounded-md border border-slate-700 bg-slate-800 px-3 text-sm text-slate-100" />
            </div>
            <Button data-testid="tir-add" onClick={addTir} className="mt-2 h-9 w-full bg-sky-600 text-white hover:bg-sky-700"><Plus className="mr-1 h-4 w-4" /> TIR AÇ</Button>
            <div className="mt-3 space-y-2">
              {tirs.map((t) => (
                <div key={t.id} data-testid={`tir-${t.id}`} className="rounded-lg border border-slate-800 bg-slate-800/40 p-2">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-slate-100">{t.plate || t.id} {t.yon && <span className="text-slate-500">· {t.yon}</span>}</span>
                    <div className="flex items-center gap-1">
                      <span className={`rounded px-1 text-[9px] font-bold ${t.closed ? "bg-rose-500/20 text-rose-300" : "bg-emerald-500/20 text-emerald-300"}`}>{t.closed ? "KAPALI" : "AÇIK"}</span>
                      <button data-testid={`tir-detail-${t.id}`} title="İÇERİK / DETAY" onClick={() => openTirDetail(t)} className="text-slate-500 hover:text-sky-400"><Eye className="h-3.5 w-3.5" /></button>
                      <button data-testid={`tir-close-${t.id}`} title={t.closed ? "AÇ" : "KAPAT"} onClick={() => toggleTir(t)} className="text-slate-500 hover:text-amber-400">{t.closed ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}</button>
                      <button data-testid={`tir-del-${t.id}`} onClick={() => delTir(t)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500">{t.driver} {t.hatlar?.length ? "· " + t.hatlar.join(", ") : ""}</div>
                  {(t.ciro > 0 || t.navlun > 0) && <div className="text-[11px] text-slate-400">CİRO: {tl(t.ciro)} · NAVLUN: {tl(t.navlun)}</div>}
                  <div className="mt-1.5"><TirSimulator yuk={t.yuk} cap_palet={t.cap_palet} cap_koli={t.cap_koli} height={72} /></div>
                  <a data-testid={`op-tir-3d-${t.id}`} href={`/tir-plan/${t.id}`} className="mt-1 inline-block rounded border border-amber-600/50 bg-amber-950/40 px-2 py-0.5 text-[11px] font-semibold text-amber-300 hover:bg-amber-900/40">3D YÜKLEME PLANI</a>
                </div>
              ))}
            </div>
          </div>
        </div>}
      </div>

      <Dialog open={!!tirDetail} onOpenChange={(v) => !v && setTirDetail(null)}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto border-slate-700 bg-slate-900 text-slate-100">
          <DialogHeader><DialogTitle className="font-head flex items-center gap-2"><Truck className="h-5 w-5 text-sky-400" /> {tirDetail?.tir.plate || tirDetail?.tir.id} — İÇERİK ({tirDetail?.pickups.length || 0})</DialogTitle></DialogHeader>
          {tirDetail && (
            <div className="space-y-3">
              <TirSimulator yuk={tirDetail.tir.yuk} cap_palet={tirDetail.tir.cap_palet} cap_koli={tirDetail.tir.cap_koli} height={120} />
              <div className="overflow-auto rounded-lg border border-slate-800">
                <table className="w-full text-xs">
                  <thead className="bg-slate-800/60 text-left uppercase tracking-wider text-slate-500"><tr><th className="px-2 py-2">TARİH</th><th className="px-2 py-2">GÖNDERİCİ</th><th className="px-2 py-2">ALICI</th><th className="px-2 py-2">VARIŞ</th><th className="px-2 py-2">TÜR</th><th className="px-2 py-2 text-right">TUTAR</th></tr></thead>
                  <tbody>
                    {tirDetail.pickups.length === 0 && <tr><td colSpan={6} className="px-2 py-8 text-center text-slate-500">ALIM YOK</td></tr>}
                    {tirDetail.pickups.map((p) => { const d = p.data || {}; return (
                      <tr key={p.id} data-testid={`tirdetail-pickup-${p.id}`} className="border-b border-slate-800/60 text-slate-200">
                        <td className="px-2 py-1.5 font-mono text-slate-400">{d.islem_tarihi}</td>
                        <td className="px-2 py-1.5">{d.gonderici_firma}</td>
                        <td className="px-2 py-1.5">{d.alici_firma || d.alici_musteri}</td>
                        <td className="px-2 py-1.5 text-slate-400">{d.alici_ilce}/{d.alici_il}</td>
                        <td className="px-2 py-1.5">{d.adet}× {d.kargo_turu}</td>
                        <td className="px-2 py-1.5 text-right">{d.tutar ? tl(d.tutar) : "—"}</td>
                      </tr>
                    ); })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)} title="ALIM SİLİNSİN Mİ?" onConfirm={del} />
      <Dialog open={!!editP} onOpenChange={(o) => !o && setEditP(null)}>
        <DialogContent data-testid="op-edit-dialog" className="max-w-3xl border-slate-700 bg-slate-900 text-slate-100">
          <DialogHeader><DialogTitle>ALIMI DÜZENLE</DialogTitle></DialogHeader>
          {editP && <KalemEditor key={editP.id} p={editP} tirs={tirs} onSaved={() => { setEditP(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
